import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

const readCookie = (name: string) => {
  var nameEQ = name + '=';
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

type HostingContext = ReturnType<typeof createSignalServer> & {};

const HostingContext = createContext<HostingContext | undefined>(undefined);

interface SessionState {
  currentSession: Session;
}

export const HostingProvider = ({
  url,
  projectId,
  children,
}: PropsWithChildren<{ url: string; projectId: string }>) => {
  const [state, setState] = useState<SessionState | null>(null);

  const signaler = createSignalServer({
    url,
    projectId,
    setSession(session) {
      setState(() => ({
        currentSession: session,
      }));
    },
  });

  // Start a new session
  useEffect(() => {
    signaler.start();
  }, []);

  return (
    <HostingContext.Provider value={{ ...signaler }}>
      {children}
    </HostingContext.Provider>
  );
};

interface Session {}

interface Event {}

const createSignalServer = ({ url, projectId, setSession }) => {
  return {
    // Start will either
    // - create a new session
    // - create a new session from a bookmark
    async start(): Promise<Session> {
      const csrfToken = decodeURIComponent(
        readCookie('X-CSRF-TOKEN') as string
      );

      const req = await fetch(`${url}/harp/hosting/${projectId}/session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify({ session: { action: 'start' } }),
      });
      const json = await req.json();

      setSession(json);
      return json;
    },

    // Pause the current Session. this will cause the application to create a bookmark
    // and store the current session.
    async pause(): Promise<boolean> {
      try {
        const csrfToken = decodeURIComponent(
          readCookie('X-CSRF-TOKEN') as string
        );

        const req = await fetch(`${url}/harp/hosting/${projectId}/session`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json',
          },
          body: JSON.stringify({ session: { action: 'pause' } }),
        });
        const json = await req.json();
        return true;
      } catch (e) {
        console.log(e);
        return false;
      }
    },

    // Resume the current session
    async resume(): Promise<boolean> {
      try {
        const csrfToken = decodeURIComponent(
          readCookie('X-CSRF-TOKEN') as string
        );

        const req = await fetch(`${url}/harp/hosting/${projectId}/session`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json',
          },
          body: JSON.stringify({ session: { action: 'resume' } }),
        });
        const json = await req.json();
        setSession(json);
        return true;
      } catch (e) {
        console.log(e);
        return false;
      }
    },

    // end should use sendBeacon as it might be called on window close
    async end(): Promise<void> {
      try {
        const csrfToken = decodeURIComponent(
          readCookie('X-CSRF-TOKEN') as string
        );

        const headers = {
          type: 'application/json',
          'X-CSRF-Token': csrfToken,
        };

        navigator.sendBeacon(
          `${url}/harp/hosting/${projectId}/session`,
          new Blob([JSON.stringify({ session: { action: 'end' } })], headers)
        );
      } catch (e) {
        console.log(e);
      }
    },
    // Gets the current session
    async current(): Promise<Session> {
      const req = await fetch(`${url}/harp/hosting/${projectId}/session`, {
        method: 'GET',
        credentials: 'include',
      });
      const json = await req.json();
      setSession(json);
      return json;
    },

    //
    async data(event: Event): Promise<void> {
      const csrfToken = decodeURIComponent(
        readCookie('X-CSRF-TOKEN') as string
      );

      const req = await fetch(`${url}/harp/hosting/${projectId}/session`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify({ session: { action: 'data', event: event } }),
      });
      const json = await req.json();
      return json;
    },
  };
};

export const useHostingSession = () => {
  const ctx = useContext(HostingContext);
  return ctx;
};
